import { isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

/**
 * 신규(CORE_OWNED) 지점의 EZREMS 연결·작업 상태 — `GET admin/native-properties/{property_id}/provider-integration-state`.
 * 기존 source 기반 provider-integration-state 와 별개 계약이며 환경·source 식별자를 추정하지 않는다.
 * 응답 키·값은 Root 후보 계약(core-native-provider-state-20261030)을 그대로 닫은 형태로만 받는다.
 */
export type NativeBindingStatus = 'BOUND' | 'UNBOUND' | 'UNAVAILABLE';
export type NativeUnavailableReason =
  | 'ROOM_NOT_CORE_OWNED'
  | 'ROOM_TYPE_UNAVAILABLE'
  | 'UNIT_SCOPE_UNAVAILABLE'
  | 'BINDING_EVIDENCE_INVALID'
  | 'NAMESPACE_UNAVAILABLE'
  | 'SCOPE_CHANGED';
export interface NativeProviderOperation {
  operation_id: string;
  action: 'room.sales.transition' | 'room.sales.first_date.change';
  target_kind: 'GENERAL' | 'MIDTERM';
  terminal_state: 'PENDING' | 'UNKNOWN' | 'APPLIED' | 'NOT_APPLIED';
  transport: 'UNKNOWN' | 'DELIVERED' | null;
  readback_matches: boolean | null;
  proof_kind: 'REMOTE_FINALITY' | 'SYNC_RESPONSE_AND_READBACK' | 'FINAL_NON_APPLICATION' | null;
  proof_observed_at: string | null;
  recorded_at: string;
}
export interface NativeProviderRoom {
  room_id: string;
  room_name: string;
  binding_status: NativeBindingStatus;
  unavailable_reason: NativeUnavailableReason | null;
  binding_visibility: 'VISIBLE' | 'REQUIRES_PROPERTY_WRITE';
  binding: {
    branch_id: number;
    branch_code: string;
    project_code: string;
    resource_id: number;
    dong: string;
    floor: string;
    ho2: string;
  } | null;
  operations: NativeProviderOperation[];
}
export interface NativeProviderState {
  property_id: string;
  provider: 'EZREMS';
  source: 'CORE_ROOM_BINDING_AND_OPERATION_JOURNAL';
  physical_state: 'NOT_OBSERVED';
  observed_at: string;
  page: { limit: number; next_cursor: string | null };
  page_summary: { rooms: number; bound: number; unbound: number; unavailable: number };
  rooms: NativeProviderRoom[];
}

const record = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);
const exact = (v: Record<string, unknown>, keys: string[]) =>
  Object.keys(v).length === keys.length && keys.every((k) => Object.hasOwn(v, k));
const oneOf = <T extends string>(list: readonly T[], v: unknown): v is T =>
  typeof v === 'string' && (list as readonly string[]).includes(v);
const count = (v: unknown): v is number =>
  typeof v === 'number' && Number.isSafeInteger(v) && v >= 0;
const positive = (v: unknown): v is number => count(v) && v > 0;
const text = (v: unknown): v is string => typeof v === 'string' && v.length > 0 && v.length <= 128;
const cursor = (v: unknown): v is string =>
  typeof v === 'string' && v.length >= 1 && v.length <= 2048 && /^[A-Za-z0-9_-]+$/.test(v);
// 오프셋 또는 Z 가 있는 ISO-8601 시각만 받는다.
const instant = (v: unknown): v is string =>
  typeof v === 'string' &&
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/.test(v) &&
  !Number.isNaN(Date.parse(v));
const invalid = () =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '지점의 이지렘스 연결 상태를 확인하지 못했습니다.',
  });

const REASONS = [
  'ROOM_NOT_CORE_OWNED',
  'ROOM_TYPE_UNAVAILABLE',
  'UNIT_SCOPE_UNAVAILABLE',
  'BINDING_EVIDENCE_INVALID',
  'NAMESPACE_UNAVAILABLE',
  'SCOPE_CHANGED',
] as const;

function operation(v: unknown): NativeProviderOperation {
  if (
    !record(v) ||
    !exact(v, [
      'operation_id',
      'action',
      'target_kind',
      'terminal_state',
      'transport',
      'readback_matches',
      'proof_kind',
      'proof_observed_at',
      'recorded_at',
    ]) ||
    !isUuid(v.operation_id) ||
    !oneOf(['room.sales.transition', 'room.sales.first_date.change'], v.action) ||
    !oneOf(['GENERAL', 'MIDTERM'], v.target_kind) ||
    !oneOf(['PENDING', 'UNKNOWN', 'APPLIED', 'NOT_APPLIED'], v.terminal_state) ||
    !(v.transport === null || oneOf(['UNKNOWN', 'DELIVERED'], v.transport)) ||
    !(v.readback_matches === null || typeof v.readback_matches === 'boolean') ||
    !(
      v.proof_kind === null ||
      oneOf(
        ['REMOTE_FINALITY', 'SYNC_RESPONSE_AND_READBACK', 'FINAL_NON_APPLICATION'],
        v.proof_kind,
      )
    ) ||
    !(v.proof_observed_at === null || instant(v.proof_observed_at)) ||
    !instant(v.recorded_at)
  )
    throw invalid();
  return v as unknown as NativeProviderOperation;
}

function room(v: unknown): NativeProviderRoom {
  if (
    !record(v) ||
    !exact(v, [
      'room_id',
      'room_name',
      'binding_status',
      'unavailable_reason',
      'binding_visibility',
      'binding',
      'operations',
    ]) ||
    !isUuid(v.room_id) ||
    !text(v.room_name) ||
    !oneOf(['BOUND', 'UNBOUND', 'UNAVAILABLE'], v.binding_status) ||
    !(v.unavailable_reason === null || oneOf(REASONS, v.unavailable_reason)) ||
    (v.binding_status === 'UNAVAILABLE') !== (v.unavailable_reason !== null) ||
    !oneOf(['VISIBLE', 'REQUIRES_PROPERTY_WRITE'], v.binding_visibility) ||
    !Array.isArray(v.operations) ||
    v.operations.length > 10 ||
    (v.binding_status !== 'BOUND' && v.operations.length > 0)
  )
    throw invalid();
  // 좌표는 쓰기 권한이 있을 때만 채워진다. 가려진 것은 미설정이 아니다.
  const hidden = v.binding_visibility === 'REQUIRES_PROPERTY_WRITE';
  if (hidden ? v.binding !== null : (v.binding_status === 'BOUND') !== (v.binding !== null))
    throw invalid();
  if (v.binding !== null) {
    const b = v.binding;
    if (
      !record(b) ||
      !exact(b, [
        'branch_id',
        'branch_code',
        'project_code',
        'resource_id',
        'dong',
        'floor',
        'ho2',
      ]) ||
      !positive(b.branch_id) ||
      !text(b.branch_code) ||
      !text(b.project_code) ||
      !positive(b.resource_id) ||
      !text(b.dong) ||
      !text(b.floor) ||
      !text(b.ho2)
    )
      throw invalid();
  }
  return { ...(v as unknown as NativeProviderRoom), operations: v.operations.map(operation) };
}

function parse(value: unknown, propertyId: string, limit: number): NativeProviderState {
  if (
    !record(value) ||
    !exact(value, [
      'property_id',
      'provider',
      'source',
      'physical_state',
      'observed_at',
      'page',
      'page_summary',
      'rooms',
    ]) ||
    typeof value.property_id !== 'string' ||
    value.property_id.toLowerCase() !== propertyId.toLowerCase() ||
    value.provider !== 'EZREMS' ||
    value.source !== 'CORE_ROOM_BINDING_AND_OPERATION_JOURNAL' ||
    value.physical_state !== 'NOT_OBSERVED' ||
    !instant(value.observed_at) ||
    !record(value.page) ||
    !exact(value.page, ['limit', 'next_cursor']) ||
    value.page.limit !== limit ||
    !(value.page.next_cursor === null || cursor(value.page.next_cursor)) ||
    !record(value.page_summary) ||
    !exact(value.page_summary, ['rooms', 'bound', 'unbound', 'unavailable']) ||
    !count(value.page_summary.rooms) ||
    !count(value.page_summary.bound) ||
    !count(value.page_summary.unbound) ||
    !count(value.page_summary.unavailable) ||
    !Array.isArray(value.rooms) ||
    value.rooms.length > limit
  )
    throw invalid();
  const rooms = value.rooms.map(room);
  const summary = value.page_summary as NativeProviderState['page_summary'];
  const tally = (status: NativeBindingStatus) =>
    rooms.filter((r) => r.binding_status === status).length;
  if (
    summary.rooms !== rooms.length ||
    summary.bound !== tally('BOUND') ||
    summary.unbound !== tally('UNBOUND') ||
    summary.unavailable !== tally('UNAVAILABLE') ||
    new Set(rooms.map((r) => r.room_id)).size !== rooms.length
  )
    throw invalid();
  return { ...(value as unknown as NativeProviderState), rooms };
}

export async function readNativeProviderState(
  client: ServiceClient,
  propertyId: string,
  options: { limit?: number; cursor?: string | null; signal?: AbortSignal } = {},
): Promise<NativeProviderState> {
  const limit = options.limit ?? 50;
  if (!isUuid(propertyId) || !Number.isInteger(limit) || limit < 1 || limit > 50) throw invalid();
  if (options.cursor != null && !cursor(options.cursor)) throw invalid();
  const value = await client.get<unknown>(
    `admin/native-properties/${propertyId}/provider-integration-state`,
    {
      headers: { 'Cache-Control': 'no-store' },
      signal: options.signal,
      params: { limit, ...(options.cursor ? { cursor: options.cursor } : {}) },
    },
  );
  return parse(value, propertyId, limit);
}
